import React, { useEffect, useRef } from 'react';
import { X, Download, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Full-screen PDF preview modal.
 *
 * Props:
 *   blobUrl    — object URL created from the PDF blob (null while loading)
 *   filename   — displayed in the top bar
 *   loading    — shows a spinner while the blob is being fetched
 *   onClose    — called when the user dismisses the modal
 *   onDownload — called when the user clicks Download (optional)
 */
export default function PdfPreviewModal({ blobUrl, filename, loading, onClose, onDownload }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex flex-col bg-black/80"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between bg-gray-900 px-4 py-3 flex-shrink-0">
        <span className="text-white text-sm font-medium truncate max-w-sm mr-4">
          {filename || 'Invoice Preview'}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onDownload && !loading && blobUrl && (
            <button
              onClick={onDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-gray-700"
            aria-label="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 bg-gray-800 min-h-0 relative">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-300">
            <Loader2 className="w-8 h-8 animate-spin mb-3" />
            <span className="text-sm">Loading preview…</span>
          </div>
        )}

        {!loading && blobUrl && (
          <embed
            src={blobUrl}
            type="application/pdf"
            className="w-full h-full"
            title="Invoice PDF Preview"
          />
        )}

        {!loading && !blobUrl && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
            <AlertCircle className="w-10 h-10 mb-3" />
            <p className="text-sm">Could not load PDF preview.</p>
            {onDownload && (
              <button
                onClick={onDownload}
                className="mt-4 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download instead
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
